import { forwardRef, useState } from 'react'
import { cn } from '@/lib/utils'
import type {
  ScorecardKpiDirection,
  ScorecardMetricDataType,
  ScorecardTargetMode,
} from '@/services/scorecard.service'

interface ScorecardTargetValueInputProps {
  id?: string
  value: number
  onChange: (value: number) => void
  onBlur?: () => void
  name?: string
  dataType?: ScorecardMetricDataType | null
  unitLabel?: string | null
  disabled?: boolean
  mode?: ScorecardTargetMode
  direction?: ScorecardKpiDirection
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** 'addon' boxes the prefix/suffix in a grey cell; default 'inline' keeps today's look. */
  affix?: 'inline' | 'addon'
}

const ADDON_CLASS =
  'flex items-center self-stretch border-primary/20 bg-muted px-3 text-primary/70'

function formatEditableNumber(
  value: number,
  dataType: ScorecardMetricDataType | null | undefined
) {
  if (!Number.isFinite(value)) return ''

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: dataType === 'PERCENTAGE' ? 2 : 4,
  }).format(value)
}

function sanitizeNumericInput(value: string) {
  const withoutAffixes = value.replace(/[,$%\s]/g, '')
  const numericOnly = withoutAffixes.replace(/[^0-9.-]/g, '')
  const isNegative = numericOnly.trim().startsWith('-')
  const unsigned = numericOnly.replace(/-/g, '')
  const [integerPart = '', ...decimalParts] = unsigned.split('.')
  const decimal = decimalParts.length > 0 ? `.${decimalParts.join('')}` : ''
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '')

  return `${isNegative ? '-' : ''}${normalizedInteger}${decimal}`
}

function isIncompleteNumericInput(value: string) {
  return value === '' || value === '-' || value === '.' || value === '-.'
}

function parseDisplayValue(value: string) {
  const sanitizedValue = sanitizeNumericInput(value)

  if (isIncompleteNumericInput(sanitizedValue)) {
    return Number.NaN
  }

  const parsed = Number(sanitizedValue)
  return Number.isFinite(parsed) ? parsed : Number.NaN
}

function formatDraftNumber(
  value: string,
  dataType: ScorecardMetricDataType | null | undefined
) {
  if (isIncompleteNumericInput(value)) return value

  const isNegative = value.startsWith('-')
  const unsigned = isNegative ? value.slice(1) : value
  const hasDecimal = unsigned.includes('.')
  const [integerPart = '', decimalPart = ''] = unsigned.split('.')
  const integerValue = Number(integerPart || '0')
  const formattedInteger = Number.isFinite(integerValue)
    ? new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 0,
      }).format(integerValue)
    : integerPart
  const maxFractionDigits = dataType === 'PERCENTAGE' ? 2 : 4
  const trimmedDecimal = decimalPart.slice(0, maxFractionDigits)

  return `${isNegative ? '-' : ''}${formattedInteger}${
    hasDecimal ? `.${trimmedDecimal}` : ''
  }`
}

function getPlaceholder(
  dataType: ScorecardMetricDataType | null | undefined,
  mode: ScorecardTargetMode | undefined
) {
  if (mode === 'PERCENT_CHANGE') return '20'
  if (dataType === 'CURRENCY') return '9,000,000'
  if (dataType === 'PERCENTAGE') return '80'
  if (dataType === 'RATIO') return '1.5'
  return '60,000'
}

export const ScorecardTargetValueInput = forwardRef<
  HTMLInputElement,
  ScorecardTargetValueInputProps
>(
  (
    {
      id,
      value,
      onChange,
      onBlur,
      name,
      dataType,
      unitLabel,
      disabled = false,
      mode,
      direction,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      affix = 'inline',
    },
    ref
  ) => {
    const [draftValue, setDraftValue] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const formattedValue = formatEditableNumber(value, dataType)
    const displayValue = isFocused ? draftValue : formattedValue
    const isPercentChange = mode === 'PERCENT_CHANGE'
    const prefix = isPercentChange
      ? direction === 'LOWER_IS_BETTER'
        ? '−'
        : '+'
      : dataType === 'CURRENCY'
        ? '$'
        : null
    const suffix = isPercentChange
      ? '%'
      : dataType === 'PERCENTAGE'
        ? '%'
        : unitLabel?.trim() || null

    return (
      <div>
        <div
          className={cn(
            'flex h-9 w-full items-center rounded-md border border-primary/20 bg-transparent text-xs transition-colors focus-within:ring-1 focus-within:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
            affix === 'addon' && 'overflow-hidden'
          )}
        >
          {prefix ? (
            <span
              className={
                affix === 'addon'
                  ? `${ADDON_CLASS} border-r`
                  : 'pl-3 pr-1 text-primary/70'
              }
              aria-hidden="true"
            >
              {prefix}
            </span>
          ) : null}
          <input
            ref={ref}
            id={id}
            name={name}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            value={displayValue}
            disabled={disabled}
            inputMode="decimal"
            placeholder={getPlaceholder(dataType, mode)}
            className="min-w-0 flex-1 bg-transparent px-3 py-1 text-primary outline-none placeholder:text-primary/70 disabled:cursor-not-allowed"
            onFocus={() => {
              setIsFocused(true)
              setDraftValue(formattedValue)
            }}
            onChange={(event) => {
              const sanitizedValue = sanitizeNumericInput(event.target.value)
              const parsedValue = parseDisplayValue(sanitizedValue)

              setDraftValue(formatDraftNumber(sanitizedValue, dataType))
              onChange(parsedValue)
            }}
            onBlur={() => {
              setIsFocused(false)
              const parsed = parseDisplayValue(displayValue)
              if (Number.isFinite(parsed)) {
                onChange(parsed)
              }
              setDraftValue('')
              onBlur?.()
            }}
          />
          {suffix ? (
            <span
              className={
                affix === 'addon'
                  ? `${ADDON_CLASS} border-l`
                  : 'pl-1 pr-3 text-primary/70'
              }
              aria-hidden="true"
            >
              {suffix}
            </span>
          ) : null}
        </div>
      </div>
    )
  }
)
ScorecardTargetValueInput.displayName = 'ScorecardTargetValueInput'
